import "./CategoryArcMenu.scss"

import React, { useCallback, useEffect, useRef, useState } from "react"
import ReactDOM from "react-dom"
import { Button, Icon } from "@fider/components"
import { classSet } from "@fider/services"
import { PostCategory } from "@fider/services/khcn"
import { CATEGORY_ICONS } from "./categoryIcons"
import { Trans } from "@lingui/react/macro"

// Tùy biến KHCN: nút "Đăng ý tưởng" kèm menu chọn hạng mục.
// - Máy tính (có hover): rê chuột vào nút thì 4 icon bung ra theo nửa vòng tròn phía dưới, rê vào icon hiện nhãn.
// - Thiết bị cảm ứng (không hover): chạm nút mở action sheet trượt từ đáy màn hình, luôn có nhãn và mô tả.
// - Bấm thẳng vào nút trên máy tính (hoặc Enter): mở form mà chưa chọn hạng mục.

interface CategoryArcMenuProps {
  categories: PostCategory[]
  disabled?: boolean
  onSelect: (slug: string) => void
  children: React.ReactNode
}

const ARC_RADIUS = 92
const ARC_START = 158 // độ, mép trái dưới
const ARC_END = 22 // độ, mép phải dưới
const CLOSE_DELAY = 180

export const CategoryArcMenu = (props: CategoryArcMenuProps) => {
  const [open, setOpen] = useState(false)
  const [touch, setTouch] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const closeTimer = useRef<number>()

  useEffect(() => {
    const query = window.matchMedia("(hover: none), (pointer: coarse)")
    const update = () => setTouch(query.matches)
    update()
    query.addEventListener("change", update)
    return () => query.removeEventListener("change", update)
  }, [])

  const cancelClose = () => window.clearTimeout(closeTimer.current)

  const close = useCallback(() => {
    cancelClose()
    setOpen(false)
  }, [])

  // Escape đóng menu ở mọi chế độ
  useEffect(() => {
    if (!open) {
      return
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        close()
      }
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [open, close])

  // Máy tính: bấm ra ngoài thì đóng vòng cung.
  // Không áp dụng cho action sheet: sheet nằm trong portal (ngoài rootRef) nên listener này
  // từng đóng sheet ngay lúc chạm vào một hạng mục, trước khi sự kiện click kịp chạy.
  useEffect(() => {
    if (!open || touch) {
      return
    }
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        close()
      }
    }
    document.addEventListener("mousedown", onPointerDown)
    return () => document.removeEventListener("mousedown", onPointerDown)
  }, [open, touch, close])

  // Cảm ứng: khóa cuộn trang phía sau khi sheet đang mở
  useEffect(() => {
    if (!open || !touch) {
      return
    }
    const previous = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = previous
    }
  }, [open, touch])

  useEffect(() => cancelClose, [])

  const scheduleClose = () => {
    cancelClose()
    closeTimer.current = window.setTimeout(() => setOpen(false), CLOSE_DELAY)
  }

  const select = (slug: string) => {
    close()
    props.onSelect(slug)
  }

  const handleMainClick = () => {
    if (props.disabled) {
      return
    }
    if (touch) {
      setOpen((current) => !current)
    } else {
      // Máy tính: bấm thẳng thì mở form, hạng mục chọn trong form
      close()
      props.onSelect("")
    }
  }

  const count = props.categories.length
  const step = count > 1 ? (ARC_START - ARC_END) / (count - 1) : 0

  return (
    <div
      ref={rootRef}
      className={classSet({ "c-arc-menu": true, "c-arc-menu--open": open, "c-arc-menu--touch": touch, "c-arc-menu--hover": !touch })}
      onMouseEnter={touch ? undefined : () => (cancelClose(), setOpen(true))}
      onMouseLeave={touch ? undefined : scheduleClose}
      onFocus={touch ? undefined : () => (cancelClose(), setOpen(true))}
      onBlur={
        touch ? undefined : (e) => (rootRef.current && e.relatedTarget && rootRef.current.contains(e.relatedTarget as Node) ? undefined : scheduleClose())
      }
    >
      <Button variant="primary" onClick={handleMainClick} disabled={props.disabled} aria-haspopup="menu" aria-expanded={open}>
        {props.children}
      </Button>

      {!touch && (
        <div className="c-arc-menu__items" role="menu" aria-hidden={!open}>
          {props.categories.map((category, index) => {
            const angle = ((ARC_START - index * step) * Math.PI) / 180
            const x = Math.round(Math.cos(angle) * ARC_RADIUS)
            const y = Math.round(Math.sin(angle) * ARC_RADIUS)
            const style = { "--x": `${x}px`, "--y": `${y}px`, "--i": index } as React.CSSProperties
            return (
              <button
                type="button"
                role="menuitem"
                key={category.slug}
                className="c-arc-menu__item"
                style={style}
                tabIndex={open ? 0 : -1}
                aria-label={category.label}
                onClick={() => select(category.slug)}
              >
                <span className="c-arc-menu__icon" style={{ color: `#${category.color}` }}>
                  <Icon sprite={CATEGORY_ICONS[category.slug]} />
                </span>
                <span className="c-arc-menu__label">{category.label}</span>
              </button>
            )
          })}
        </div>
      )}

      {touch &&
        open &&
        ReactDOM.createPortal(
          <div className="c-arc-menu__overlay" onClick={close}>
            <div className="c-arc-menu__sheet" role="menu" onClick={(e) => e.stopPropagation()}>
              <div className="c-arc-menu__sheet-head">
                <div className="c-arc-menu__sheet-handle" aria-hidden="true" />
                <div className="c-arc-menu__sheet-title">
                  <Trans id="khcn.arc.sheet.title">What would you like to post?</Trans>
                </div>
              </div>
              {props.categories.map((category) => (
                <button type="button" role="menuitem" key={category.slug} className="c-arc-menu__sheet-row" onClick={() => select(category.slug)}>
                  <span className="c-arc-menu__sheet-icon" style={{ color: `#${category.color}` }}>
                    <Icon sprite={CATEGORY_ICONS[category.slug]} />
                  </span>
                  <span className="c-arc-menu__sheet-text">
                    <span className="c-arc-menu__sheet-label">{category.label}</span>
                    <span className="c-arc-menu__sheet-hint">{category.hint}</span>
                  </span>
                </button>
              ))}
              <button type="button" className="c-arc-menu__sheet-cancel" onClick={close}>
                <Trans id="action.cancel">Cancel</Trans>
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  )
}
